import React, { useEffect, useRef, useState } from 'react';
import { getProtocol } from '../data/protocols';
import { getSession } from '../data/guidedSessions';
import { Segment } from '../data/runs';
import { planString } from '../services/runFormat';
import { useLanguage } from '../i18n/LanguageContext';
import RunSurface from './RunSurface';
import { AURORA } from '../data/runTheme';

interface TransitionBufferProps {
  segment: Segment;
  seconds?: number;
  onDone: () => void;
  onQuit: () => void;
  onPauseChange?: (paused: boolean) => void;
}

const pill: React.CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 500,
  padding: '0.6rem 1.5rem',
  borderRadius: '999px',
  cursor: 'pointer',
  border: '1px solid rgba(255,255,255,0.28)',
  background: 'rgba(255,255,255,0.12)',
  color: AURORA.textPrimary,
};

const TransitionBuffer: React.FC<TransitionBufferProps> = ({ segment, seconds = 10, onDone, onQuit, onPauseChange }) => {
  const { language, t } = useLanguage();
  const pick = (b: { no: string; en: string }) => (language === 'en' ? b.en : b.no);
  const [left, setLeft] = useState(seconds);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const doneRef = useRef(false);

  useEffect(() => {
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setLeft((v) => (v <= 1 ? 0 : v - 1));
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire onDone once the countdown reaches 0 — in an effect, not inside the
  // setLeft updater (which would call setState during another render).
  useEffect(() => {
    if (left <= 0 && !doneRef.current) {
      doneRef.current = true;
      onDone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left]);

  const skip = () => { if (!doneRef.current) { doneRef.current = true; onDone(); } };
  const togglePause = () => {
    const next = !pausedRef.current;
    pausedRef.current = next;
    setPaused(next);
    onPauseChange?.(next);
  };

  const protocol = segment.kind === 'protocol' ? getProtocol(segment.id) : undefined;
  const session = segment.kind === 'session' ? getSession(segment.id) : undefined;
  const name = protocol ? pick(protocol.name) : session ? pick(session.name) : '';
  const detail = protocol
    ? pick(planString(protocol))
    : session
      ? `${pick(session.durationLabel)} · ${session.phases.length} ${t('protocols.phasesWord')}`
      : '';

  return (
    <RunSurface>
      <p style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: AURORA.textMuted, marginBottom: '0.5rem' }}>{t('protocols.next')}</p>
      <h2 style={{ fontSize: '1.875rem', fontWeight: 600, color: AURORA.textPrimary, marginBottom: '0.5rem' }}>{name}</h2>
      <p style={{ color: AURORA.textSecondary, marginBottom: '1rem', maxWidth: '28rem', textAlign: 'center', padding: '0 1rem' }}>{detail}</p>
      {protocol?.safety && (
        <p style={{ fontSize: '0.875rem', color: '#FCD9A8', background: 'rgba(255,255,255,0.08)', borderRadius: '1rem', padding: '0.75rem 1rem', maxWidth: '28rem', margin: '0 1rem 1rem', textAlign: 'center' }}>
          {pick(protocol.safety)}
        </p>
      )}
      <p style={{ color: AURORA.textSecondary, marginBottom: '1.5rem' }}>{t('protocols.breatheNormally')}</p>
      <div style={{ fontSize: '3.75rem', fontWeight: 300, color: AURORA.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{left}</div>
      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        <button style={pill} onClick={togglePause}>{paused ? t('protocols.resume') : t('protocols.pause')}</button>
        <button style={pill} onClick={skip}>{t('protocols.startNow')}</button>
        <button style={pill} onClick={onQuit}>{t('protocols.toMenu')}</button>
      </div>
    </RunSurface>
  );
};

export default TransitionBuffer;
